import { memo, useState } from 'react';
import Image from 'next/image';
import scheduleDemoBackground from '@assets/images/scheduleDemoBackground.png';
import classNames from 'classnames';
import { TextField } from '@mui/material';
import { CTAButton } from '@components';
import { Controller, useForm } from 'react-hook-form';
import { ISendEmailPayload } from '@types';
import { usePostSendEmail } from '@api/hooks';
import { useRouter } from 'next/router';
import styles from './styles.module.scss';

const ScheduleDemo = memo(() => {
  const [ctaLabel, setCtaLabel] = useState<'Schedule a demo' | 'Sent!'>('Schedule a demo');

  const router = useRouter();
  const { handleSubmit, control, reset } = useForm<ISendEmailPayload>({
    defaultValues: {
      email: '',
    },
  });

  const { mutate, isLoading } = usePostSendEmail({
    onSuccess: () => {
      setCtaLabel('Sent!');
      reset();
    },
  });

  const handleSubmission = (data: ISendEmailPayload) => {
    mutate({ type: 'scheduleDemo', page: router.asPath, ...data });
  };

  return (
    <div className={classNames(styles.scheduleDemo, styles.scheduleDemoContainer)}>
      <Image
        alt="bg-hero-image"
        src={scheduleDemoBackground}
        priority
        className={styles.fullScreenAbsolute}
      />

      <div className={classNames(styles.scheduleDemoContainer, styles.scheduleDemoTitle)}>
        <h1>
          🗓 <span>Schedule a Demo</span>
        </h1>
        <p>Schedule the best date and time for the demo now.</p>
      </div>
      <form onSubmit={handleSubmit(handleSubmission)} className={styles.scheduleDemoForm}>
        <Controller
          control={control}
          name="email"
          rules={{
            required: true,
            pattern: {
              value: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
              message: 'Invalid Email Format',
            },
          }}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <TextField
              fullWidth
              InputLabelProps={{ shrink: true }}
              placeholder="Email Address"
              error={!!error}
              inputRef={ref}
              disabled={isLoading}
              onKeyDown={() => setCtaLabel('Schedule a demo')}
              {...field}
            />
          )}
        />
        <CTAButton
          type="submit"
          className={styles.submit}
          variant="outlined"
          isLoading={isLoading}
        >
          {ctaLabel}
        </CTAButton>
      </form>
    </div>
  );
});

ScheduleDemo.displayName = 'ScheduleDemo';

export default ScheduleDemo;
